import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { useAppDispatch, useAppSelector } from "@/store";
import { extractErrorMessage } from "@api/axios";
import { createShareLink } from "@/store/shareLinksSlice";
import { SHARE_LINK_PASSWORD_MIN_LENGTH } from "@/constants/app";

interface ShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ShareLinkDialog({ open, onClose }: ShareLinkDialogProps) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const { files } = useAppSelector((s) => s.files);

  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [maxDownloads, setMaxDownloads] = useState(1);
  const [expiresAt, setExpiresAt] = useState("");
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleToggleFile = (fileId: string) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId],
    );
  };

  const handleCreate = async () => {
    if (selectedFiles.length === 0) return;
    if (password.length > 0 && password.length < SHARE_LINK_PASSWORD_MIN_LENGTH) {
      notify(t("shareLink.passwordTooShort", { min: SHARE_LINK_PASSWORD_MIN_LENGTH }), "error");
      return;
    }
    setSubmitting(true);
    try {
      const result = await dispatch(
        createShareLink({
          fileIds: selectedFiles,
          password: password || undefined,
          expiresAt: expiresAt || undefined,
          maxDownloads,
        }),
      ).unwrap();
      setCreatedLink(`${window.location.origin}/s/${result.token}`);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setPassword("");
    setMaxDownloads(1);
    setExpiresAt("");
    setCreatedLink(null);
    onClose();
  };

  const handleCopy = () => {
    if (createdLink) {
      navigator.clipboard.writeText(createdLink);
      notify(t("shareLink.copied"), "success");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("shareLink.create")}</DialogTitle>
      <DialogContent>
        {createdLink ? (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                color: "text.secondary",
              }}
            >
              Your share link is ready:
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                bgcolor: "background.default",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>
                {createdLink}
              </Typography>
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              {t("shareLink.selectFiles")}
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: "auto" }}>
              {files.map((file) => (
                <FormControlLabel
                  key={file.id}
                  control={
                    <Checkbox
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => handleToggleFile(file.id)}
                    />
                  }
                  label={`${file.originalName} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`}
                  sx={{ display: "block" }}
                />
              ))}
            </Box>

            <TextField
              label={t("shareLink.password")}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              fullWidth
              helperText={t("shareLink.passwordHint", { min: SHARE_LINK_PASSWORD_MIN_LENGTH })}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label={t("shareLink.expiry")}
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label={t("shareLink.maxDownloads")}
              type="number"
              value={maxDownloads}
              onChange={(e) => setMaxDownloads(Math.max(1, parseInt(e.target.value) || 1))}
              size="small"
              fullWidth
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {createdLink ? t("common.close") : t("common.cancel")}
        </Button>
        {!createdLink && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreate}
            disabled={selectedFiles.length === 0 || submitting}
          >
            {t("shareLink.create")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
